import React, { useEffect, useMemo, useState } from 'react';
import { useStore, useTheme } from './state/store';
import { MORE_NAV, NAV } from './lib/constants';
import { fmtDate } from './lib/dates';
import { d001Surfacing } from './lib/operatingMode';

import Command from './pages/Command';
import Actions from './pages/Actions';
import Pipeline from './pages/Pipeline';
import CommercialPipeline from './pages/CommercialPipeline';
import Dubai from './pages/Dubai';
import Saudi from './pages/Saudi';
import Infrastructure from './pages/Infrastructure';
import Evidence from './pages/Evidence';
import Runway from './pages/Runway';
import Plan from './pages/Plan';
import Review from './pages/Review';
import Decisions from './pages/Decisions';
import Settings from './pages/Settings';
import Gates from './pages/Gates';
import Strategy from './pages/Strategy';
import Diagnostics from './pages/Diagnostics';
import PlanConfigurator from './pages/PlanConfigurator';

import EvidenceForm from './components/EvidenceForm';
import { CloseoutForm, ExceptionForm } from './components/Forms';
import { UserMenu } from './components/UserMenu';
import { AskTheBridgeButton } from './components/TheBridge';
import { NAV_ICONS, CollapseIcon } from './components/NavIcons';
import { MobileShell } from './components/MobileShell';
import { useIsMobile } from './hooks/useMediaQuery';

const NAV_COLLAPSE_KEY = 'bridge45.navCollapsed';

function readNavCollapsed() {
  try {
    return localStorage.getItem(NAV_COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

const PAGES = {
  command: Command,
  actions: Actions,
  pipeline: Pipeline,
  'primary-deal': Dubai,
  dubai: Dubai, // legacy hash → remapped in readHash
  saudi: Saudi,
  infrastructure: Infrastructure,
  evidence: Evidence,
  runway: Runway,
  plan: Plan,
  review: Review,
  decisions: Decisions,
  settings: Settings,
  gates: Gates,
  strategy: Strategy,
  diagnostics: Diagnostics,
  'plan-configurator': PlanConfigurator,
  // legacy hash aliases → commercial pipeline (prefer #/pipeline + named tabs)
  'dubai-pipeline': CommercialPipeline,
};

const MORE_KEYS = new Set(MORE_NAV.map((n) => n.key));

function readHash() {
  const h = String(window.location.hash || '').replace('#/', '').replace('#', '');
  // More is a nav dropdown, not a page — legacy #/more → Settings
  if (h === 'more') return 'settings';
  // Legacy Blocks deep-workspace hash
  if (h === 'dubai') return 'primary-deal';
  return PAGES[h] ? h : 'command';
}

export default function App({ onLogout, session }) {
  const { d, state, set, pending, error, reload } = useStore();
  const [page, setPage] = useState(readHash);
  const [modal, setModal] = useState(null);
  const [navCollapsed, setNavCollapsed] = useState(readNavCollapsed);
  const [moreOpen, setMoreOpen] = useState(() => MORE_KEYS.has(readHash()));
  const isMobile = useIsMobile();
  const theme = state.settings?.theme || 'system';
  useTheme(theme);
  const surfacing = d001Surfacing(d);
  const moreItems = useMemo(
    () => MORE_NAV.filter((item) => (item.d001 ? surfacing.showInNav : true)),
    [surfacing.showInNav],
  );
  const moreActive = MORE_KEYS.has(page);

  useEffect(() => {
    if (moreActive) setMoreOpen(true);
  }, [moreActive]);

  useEffect(() => {
    const onHash = () => setPage(readHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(NAV_COLLAPSE_KEY, navCollapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [navCollapsed]);

  const go = (key) => {
    const next = key === 'more' ? 'settings' : key;
    window.location.hash = `/${next}`;
    setPage(next);
    window.scrollTo({ top: 0 });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.target && /input|textarea|select/i.test(e.target.tagName)) return;
      if (e.code === 'BracketLeft' && !e.metaKey && !e.ctrlKey && !isMobile) {
        setNavCollapsed((v) => !v);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isMobile]);

  const Page = PAGES[page] || Command;
  const alertCount = d.alerts.length;
  const nav = NAV.find((n) => n.key === page);
  const moreLabel = MORE_NAV.find((n) => n.key === page)?.label;
  const pageTitle = nav?.label || moreLabel || 'Bridge45';
  const preSprint = d.sprint.phase === 'pre';
  const dayLabel = preSprint ? `T−${d.sprint.daysUntilStart}` : `D${d.sprint.day}`;

  if (isMobile) {
    return (
      <MobileShell
        session={session}
        onLogout={onLogout}
        page={page}
        go={go}
        modal={modal}
        setModal={setModal}
        error={error}
        reload={reload}
      />
    );
  }

  return (
    <div className={`app${navCollapsed ? ' nav-collapsed' : ''}`}>
      <nav className={`nav${navCollapsed ? ' collapsed' : ''}`} aria-label="Primary">
        <div className="nav-brand">
          <div className="nav-brand-row">
            <h1>{navCollapsed ? 'B45' : 'Bridge45'}</h1>
            <button
              type="button"
              className="nav-collapse-btn desktop-only"
              onClick={() => setNavCollapsed((v) => !v)}
              aria-label={navCollapsed ? 'Expand side navigation' : 'Collapse side navigation'}
              title={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
            >
              <CollapseIcon collapsed={navCollapsed} />
            </button>
          </div>
          {!navCollapsed && <div className="tag">Zero → Proof → Scale</div>}
        </div>

        <div className="nav-day" title={preSprint ? `Pre-sprint · execution begins ${fmtDate(d.sprint.start)}` : d.sprint.label}>
          <div className="big">{navCollapsed ? dayLabel : (preSprint ? `PRE · T−${d.sprint.daysUntilStart}` : `DAY ${d.sprint.day} / 45`)}</div>
          {!navCollapsed && (
            <>
              <div className="sub">
                {preSprint
                  ? `Execution begins ${fmtDate(d.sprint.start)}`
                  : `${d.sprint.label} · ${d.sprint.daysRemaining} days left`}
              </div>
              <div className="bar">
                <span style={{ width: `${preSprint ? 0 : d.sprint.pctElapsed}%` }} />
              </div>
            </>
          )}
        </div>

        <div className="nav-items">
          {NAV.map((item, i) => {
            const idx = String(i + 1).padStart(2, '0');
            const name = `${idx} ${item.label}`;
            if (item.key === 'more') {
              return (
                <div key="more" className={`nav-more${moreOpen ? ' open' : ''}`}>
                  <button
                    type="button"
                    className={`nav-item ${moreActive || moreOpen ? 'active' : ''}`}
                    onClick={() => setMoreOpen((v) => !v)}
                    aria-label={name}
                    aria-expanded={moreOpen}
                    aria-haspopup="menu"
                    title={navCollapsed ? item.label : undefined}
                    data-tooltip={item.label}
                  >
                    <span className="nav-icon">{NAV_ICONS.more}</span>
                    <span className="idx">{idx}</span>
                    <span className="nav-label">{item.label}</span>
                    {!navCollapsed && (
                      <span className="nav-caret" aria-hidden="true">{moreOpen ? '▾' : '▸'}</span>
                    )}
                  </button>
                  {moreOpen && (
                    <div className="nav-submenu" role="menu">
                      {moreItems.map((sub) => (
                        <button
                          key={sub.key}
                          type="button"
                          role="menuitem"
                          className={`nav-subitem${page === sub.key ? ' active' : ''}`}
                          onClick={() => go(sub.key)}
                        >
                          {sub.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            const active = page === item.key
              || (item.key === 'pipeline' && ['saudi', 'infrastructure'].includes(page))
              || (item.key === 'strategy' && ['plan', 'decisions'].includes(page));
            return (
              <button
                key={item.key}
                type="button"
                className={`nav-item ${active ? 'active' : ''}`}
                onClick={() => go(item.key)}
                aria-label={name}
                title={navCollapsed ? item.label : undefined}
                data-tooltip={item.label}
              >
                <span className="nav-icon">{NAV_ICONS[item.key] || NAV_ICONS.command}</span>
                <span className="idx">{idx}</span>
                <span className="nav-label">{item.label}</span>
                {item.key === 'command' && alertCount > 0 && <span className="count">{alertCount}</span>}
              </button>
            );
          })}
        </div>

        <div className="nav-foot">
          {!navCollapsed && (
            <span className={`lock-chip${d.sprint.strategyApprovedAt ? '' : ' lock-chip-unset'}`}>
              {d.sprint.lockLabel}
            </span>
          )}
          <div
            className={`saved ${error ? 'bad' : ''}`}
            title={error ? 'Not saved — API error' : pending > 0 ? `Saving ${pending}…` : 'Saved'}
          >
            {navCollapsed
              ? (error ? '!' : pending > 0 ? '…' : '✓')
              : (error ? 'Not saved — API error' : pending > 0 ? `Saving ${pending}…` : 'Saved')}
          </div>
        </div>
      </nav>

      <main className="main">
        <header className="topbar">
          <h2>{pageTitle}</h2>
          <span className="chip">{fmtDate(d.sprint.today, { year: true })}</span>
          {d.sprint.nextGate && (
            <button className="btn sm ghost" onClick={() => go('gates')}>
              Next gate: {d.sprint.nextGate.label}
            </button>
          )}
          <div className="spacer" />
          <AskTheBridgeButton />
          <UserMenu
            session={session}
            theme={theme}
            onThemeChange={(v) => set('settings.theme', v)}
            onManageProfile={() => go('settings')}
            onLogout={onLogout}
          />
        </header>

        {error && (
          <div className="api-error">
            <strong>A change did not reach the API.</strong>{' '}
            {error.message}
            {error.requestId ? ` (request ${error.requestId})` : ''}
            <button className="btn sm" onClick={reload}>Reload from API</button>
          </div>
        )}

        <div className="page">
          <Page go={go} openModal={setModal} />
        </div>
      </main>

      {modal === 'evidence' && <EvidenceForm onClose={() => setModal(null)} />}
      {modal === 'closeout' && <CloseoutForm onClose={() => setModal(null)} />}
      {modal === 'exception' && <ExceptionForm onClose={() => setModal(null)} />}
    </div>
  );
}
